import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../api";
import Avatar from "../components/ui/Avatar";
import {
  Users2, Search, Mail, Building2, Briefcase, Link2,
  Trash2, X as IconX, Loader2, Filter, CheckCircle2, AlertCircle,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

const SOURCE_OPTIONS = [
  { v: "",         lbl: "All sources" },
  { v: "manual",   lbl: "Manual upload" },
  { v: "referral", lbl: "Referral" },
  { v: "linkedin", lbl: "LinkedIn" },
  { v: "walkin",   lbl: "Walk-in" },
  { v: "agency",   lbl: "Agency" },
  { v: "event",    lbl: "Event / Career fair" },
];

const SOURCE_BADGE = (src) => {
  const map = {
    manual:   "bg-purple-50 text-purple-700 border-purple-200",
    referral: "bg-emerald-50 text-emerald-700 border-emerald-200",
    linkedin: "bg-blue-50 text-blue-700 border-blue-200",
    walkin:   "bg-amber-50 text-amber-700 border-amber-200",
    agency:   "bg-rose-50 text-rose-700 border-rose-200",
    event:    "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return map[src] || "bg-paper text-muted border-rule";
};

function AttachModal({ candidate, postings, onClose, onAttached }) {
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!pid) return setErr("Pick a posting first.");
    setBusy(true); setErr("");
    try {
      await api.post(`/me/sourced-candidates/${candidate.id}/attach`,
                     { posting_id: pid });
      onAttached?.();
    } catch (e2) {
      setErr(formatError(e2) || "Could not attach.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="attach-modal">
      <div className="bg-white rounded-md w-full max-w-md shadow-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-rule">
          <div>
            <div className="eyebrow">Attach to another posting</div>
            <h3 className="font-display font-bold text-lg mt-0.5">{candidate.name}</h3>
          </div>
          <button className="btn-ghost" onClick={onClose} aria-label="Close"
                  data-testid="attach-modal-close"><IconX size={14}/></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs text-muted">Pick a posting</label>
            <select className="input" value={pid} onChange={(e)=>setPid(e.target.value)}
                    data-testid="attach-modal-select">
              <option value="">— Select —</option>
              {postings.map(p => (
                <option key={p.id} value={p.id}>{p.title}{p.status ? ` · ${p.status}` : ""}</option>
              ))}
            </select>
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="attach-modal-error">{err}</div>}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-rule">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={busy}
                    data-testid="attach-modal-submit">
              {busy ? <><Loader2 size={12} className="animate-spin"/> Attaching…</>
                    : <><Link2 size={12}/> Attach</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CompanySourcedTalent() {
  usePageTitle("Sourced Talent");
  const [list, setList] = useState([]);
  const [postings, setPostings] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [attachFor, setAttachFor] = useState(null);
  const [busyDel, setBusyDel] = useState("");
  const [notice, setNotice] = useState(null); // {type:'ok'|'err', text:string}

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (source)   params.source = source;
      const r = await api.get("/me/sourced-candidates", { params });
      setList(r.data || []);
    } catch (e) {
      // Non-critical: the spinner stays off and the table just shows the
      // last good state. Logged for devtools but no toast (the search bar
      // re-fetches on every keystroke; a transient blip self-heals).
      if (process.env.NODE_ENV !== "production") {
        console.warn("[sourced-talent] list fetch failed:", e?.message || e);
      }
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Re-fetch when filters change (debounced lightly via useEffect)
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [q, source]);

  useEffect(() => {
    api.get("/postings/mine").then(r => setPostings(r.data || [])).catch(()=>{});
  }, []);

  const totals = useMemo(() => {
    const bySource = {};
    let totalApps = 0;
    for (const c of list) {
      const s = c.source || "manual";
      bySource[s] = (bySource[s] || 0) + 1;
      totalApps += c.applications_count || 0;
    }
    return { count: list.length, totalApps, bySource };
  }, [list]);

  const removeCandidate = async (c) => {
    if (!window.confirm(
      `Remove ${c.name} from your talent pool? ` +
      `This will also remove their ${c.applications_count} application(s).`
    )) return;
    setBusyDel(c.id);
    try {
      await api.delete(`/me/sourced-candidates/${c.id}`);
      setList((xs) => xs.filter((x) => x.id !== c.id));
      setNotice({ type: "ok", text: `${c.name} removed from your talent pool.` });
      setTimeout(() => setNotice(null), 3500);
    } catch (e) {
      setNotice({ type: "err", text: formatError(e) || `Could not remove ${c.name}.` });
      setTimeout(() => setNotice(null), 5000);
    } finally { setBusyDel(""); }
  };

  return (
    <div className="space-y-5" data-testid="sourced-talent-page">
      {notice && (
        <div
          className={[
            "rounded-sm border px-3 py-2 text-sm flex items-center gap-2",
            notice.type === "ok"
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-red-50 border-red-200 text-red-700",
          ].join(" ")}
          data-testid="sourced-notice"
        >
          {notice.type === "ok" ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>}
          {notice.text}
        </div>
      )}
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><Users2 size={12}/> Talent pool</span>
          <h1 className="font-display font-black text-4xl mt-1">Sourced candidates</h1>
          <p className="text-muted text-sm mt-1 max-w-xl">
            Every candidate you&apos;ve added manually across all postings, in one place.
            Reuse a resume on a new role with one click — no re-upload needed.
          </p>
        </div>
        <div className="card card-pad flex items-center gap-4 flex-wrap">
          <div>
            <div className="eyebrow">Candidates</div>
            <div className="font-display font-bold text-3xl" data-testid="sourced-kpi-count">{totals.count}</div>
          </div>
          <div className="h-10 w-px bg-rule mx-1"/>
          <div>
            <div className="eyebrow">Active applications</div>
            <div className="font-display font-bold text-3xl" data-testid="sourced-kpi-apps">{totals.totalApps}</div>
          </div>
        </div>
      </header>

      {/* Filter bar */}
      <div className="card card-pad flex items-center gap-3 flex-wrap" data-testid="sourced-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-muted"/>
          <input
            className="input pl-9"
            placeholder="Search by name, email or company…"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            data-testid="sourced-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={12} className="text-muted"/>
          <select className="input max-w-xs" value={source} onChange={(e)=>setSource(e.target.value)}
                  data-testid="sourced-source-filter">
            {SOURCE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.lbl}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto" data-testid="sourced-table-wrap">
        {loading ? (
          <div className="p-10 text-center text-muted">
            <Loader2 size={18} className="animate-spin inline mr-2"/> Loading…
          </div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-muted" data-testid="sourced-empty">
            <Users2 size={28} className="mx-auto text-muted/60 mb-3"/>
            <div className="font-medium text-default">No sourced candidates yet</div>
            <div className="text-sm mt-1">
              Open any posting under <b>My Postings</b> and click <b>“+ Add candidate”</b> to source someone manually.
            </div>
          </div>
        ) : (
          <table className="dense" data-testid="sourced-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Current role</th>
                <th>Source</th>
                <th>Postings</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map(c => (
                <tr key={c.id} data-testid={`sourced-row-${c.id}`}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      <Avatar name={c.name} size={32}/>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted truncate inline-flex items-center gap-1">
                          <Mail size={10}/> {c.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="text-sm truncate max-w-[180px]">{c.current_role || "—"}</div>
                    {c.current_company && (
                      <div className="text-xs text-muted truncate inline-flex items-center gap-1 max-w-[180px]">
                        <Building2 size={10}/> {c.current_company}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-sm border uppercase ${SOURCE_BADGE(c.source || "manual")}`}>
                      {c.source || "manual"}
                    </span>
                  </td>
                  <td>
                    <div className="inline-flex items-center gap-1 text-sm">
                      <Briefcase size={11} className="text-muted"/>
                      <b>{c.applications_count}</b>
                    </div>
                    {c.applications_count > 0 && (
                      <div className="text-[10px] text-muted truncate max-w-[200px]">
                        {(c.applications || []).slice(0, 2).map(a => a.posting_title).join(" · ")}
                        {(c.applications || []).length > 2 ? ` +${c.applications.length - 2}` : ""}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="text-xs text-muted">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <button className="btn-ghost" onClick={()=>setAttachFor(c)}
                              data-testid={`sourced-attach-${c.id}`}>
                        <Link2 size={12}/> Attach to role
                      </button>
                      <button className="btn-ghost text-red-600 hover:text-red-700"
                              onClick={()=>removeCandidate(c)}
                              disabled={busyDel === c.id}
                              data-testid={`sourced-delete-${c.id}`}
                              aria-label="Remove candidate">
                        {busyDel === c.id
                          ? <Loader2 size={12} className="animate-spin"/>
                          : <Trash2 size={12}/>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {attachFor && (
        <AttachModal
          candidate={attachFor}
          postings={postings}
          onClose={()=>setAttachFor(null)}
          onAttached={()=>{ setAttachFor(null); load(); }}
        />
      )}
    </div>
  );
}
